import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { crmHeaders } from "../utils/crmApi";
import { playNotificationSound } from "../utils/playNotificationSound";
import { hasAnyEmailAccess, hasFullAccess } from "../utils/userAccess";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const POLL_INTERVAL_MS = 120000;
const IMAP_BATCH_SIZE = 2;

const EmailNotificationContext = createContext({
    byCountry: {},
    byEmail: {},
    totalUnread: 0,
    refresh: () => {},
});

async function processInBatches(items, batchSize, fn) {
    for (let i = 0; i < items.length; i += batchSize) {
        const batch = items.slice(i, i + batchSize);
        await Promise.all(batch.map(fn));
    }
}

function showNewEmailToast(accountEmail, count) {
    toast.info(
        <div className="email-notif-popup">
            <div className="email-notif-popup-icon">
                <i className="bi bi-envelope-fill"></i>
            </div>
            <div className="email-notif-popup-body">
                <strong>
                    {count > 1 ? `${count} new emails` : "New email"} — {accountEmail}
                </strong>
                <small>Open Inbox to read</small>
            </div>
        </div>,
        {
            autoClose: 7000,
            hideProgressBar: false,
            closeOnClick: true,
            icon: false,
        }
    );
}

export function EmailNotificationProvider({ children }) {
    const [byCountry, setByCountry] = useState({});
    const [byEmail, setByEmail] = useState({});
    const [totalUnread, setTotalUnread] = useState(0);
    const initializedRef = useRef(false);
    const prevByEmailRef = useRef({});

    const refresh = useCallback(async () => {
        if (!Cookies.get("username")) return;
        if (!hasFullAccess() && !hasAnyEmailAccess()) return;
        if (typeof document !== "undefined" && document.hidden) return;

        try {
            const res = await axios.get(`${BASE_URL}/getemails`, { headers: crmHeaders() });
            const accounts = res.data || [];
            const countryCounts = {};
            const emailCounts = {};
            let total = 0;

            await processInBatches(accounts, IMAP_BATCH_SIZE, async (acc) => {
                try {
                    const unreadRes = await axios.post(
                        `${BASE_URL}/getunreadcount`,
                        { Email: acc.Email },
                        { headers: crmHeaders(), timeout: 25000 }
                    );
                    const unread = unreadRes.data?.unseen || 0;
                    const countryKey = acc.Country || "Other";
                    const prevUnread = prevByEmailRef.current[acc.Email] ?? 0;

                    emailCounts[acc.Email] = unread;
                    countryCounts[countryKey] = (countryCounts[countryKey] || 0) + unread;
                    total += unread;

                    if (initializedRef.current && unread > prevUnread) {
                        playNotificationSound();
                        showNewEmailToast(acc.Email, unread - prevUnread);
                    }

                    prevByEmailRef.current[acc.Email] = unread;
                } catch {
                    /* skip accounts that fail to connect */
                }
            });

            setByCountry(countryCounts);
            setByEmail(emailCounts);
            setTotalUnread(total);
            initializedRef.current = true;
        } catch {
            /* ignore poll errors */
        }
    }, []);

    useEffect(() => {
        if (!Cookies.get("username")) return undefined;
        if (!hasFullAccess() && !hasAnyEmailAccess()) return undefined;

        refresh();
        const timer = setInterval(refresh, POLL_INTERVAL_MS);

        const onVisibility = () => {
            if (!document.hidden) refresh();
        };
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            clearInterval(timer);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [refresh]);

    const value = useMemo(
        () => ({ byCountry, byEmail, totalUnread, refresh }),
        [byCountry, byEmail, totalUnread, refresh]
    );

    return (
        <EmailNotificationContext.Provider value={value}>{children}</EmailNotificationContext.Provider>
    );
}

export function EmailCountBadge({ count, className = "" }) {
    const n = Number(count) || 0;
    if (n <= 0) return null;
    const label = n > 99 ? "99+" : String(n);
    return (
        <span
            className={`email-notif-badge ${className}`.trim()}
            aria-label={`${n} unread emails`}
        >
            {label}
        </span>
    );
}

export function useEmailNotifications() {
    return useContext(EmailNotificationContext);
}
